// Token validation and utility functions

// Single source of truth for session lifetime
export const SESSION_LIFETIME_MS = 24 * 60 * 60 * 1000; // 24 hours
export const TOKEN_LIFETIME_MS = 30 * 60 * 1000;        // 30 minutes (Salesforce token)

export class TokenUtils {
  /**
   * Calculate time until token expires in milliseconds
   */
  static getTimeUntilExpiry(creationDate) {
    if (!creationDate) return 0;
    
    const tokenCreatedAt = new Date(creationDate).getTime();
    const tokenAge = Date.now() - tokenCreatedAt;
    
    return Math.max(0, TOKEN_LIFETIME_MS - tokenAge);
  }

  /**
   * Check if token needs refresh (5 minutes before expiry)
   */
  static shouldRefreshToken(creationDate, refreshThreshold = 5 * 60 * 1000) {
    const timeUntilExpiry = this.getTimeUntilExpiry(creationDate);
    return timeUntilExpiry <= refreshThreshold;
  }

  /**
   * Check if token is expired
   */
  static isTokenExpired(creationDate) {
    const timeUntilExpiry = this.getTimeUntilExpiry(creationDate);
    return timeUntilExpiry <= 0;
  }

  /**
   * Get token expiry date
   */
  static getTokenExpiryDate(creationDate) {
    if (!creationDate) return null;
    
    const tokenCreatedAt = new Date(creationDate).getTime();
    const tokenLifetime = TOKEN_LIFETIME_MS; // 30 minutes
    
    return new Date(tokenCreatedAt + tokenLifetime);
  }

  /**
   * Format time remaining until expiry
   */
  static formatTimeUntilExpiry(creationDate) {
    const timeUntilExpiry = this.getTimeUntilExpiry(creationDate);
    
    if (timeUntilExpiry <= 0) return 'Expired';
    
    const minutes = Math.floor(timeUntilExpiry / (60 * 1000));
    const seconds = Math.floor((timeUntilExpiry % (60 * 1000)) / 1000);
    
    return `${minutes}m ${seconds}s`;
  }

  /**
   * Get token health status
   */
  static getTokenHealthStatus(creationDate) {
    if (!creationDate) {
      return { status: 'unknown', message: 'No token creation date' };
    }

    const timeUntilExpiry = this.getTimeUntilExpiry(creationDate);
    
    if (timeUntilExpiry <= 0) {
      return { status: 'expired', message: 'Token has expired' };
    }
    
    if (timeUntilExpiry <= 5 * 60 * 1000) { // 5 minutes
      return { status: 'warning', message: 'Token expires soon' };
    }
    
    if (timeUntilExpiry <= 10 * 60 * 1000) { // 10 minutes
      return { status: 'caution', message: 'Token will expire in 10 minutes' };
    }
    
    return { status: 'healthy', message: 'Token is valid' };
  }

  /**
   * Extract username/email from Redux store
   */
  static getUsernameFromStore() {
    try {
      const persistedData = localStorage.getItem('persist:root');
      if (persistedData) {
        const root = JSON.parse(persistedData);
        const userDetails = JSON.parse(root.userDetails || '{}');
        // Use Email as the primary identifier for token refresh
        return userDetails?.details?.Email;
      }
    } catch (error) {
      console.error('Error getting username from store:', error);
    }
    return null;
  }

  /**
   * Get token state from Redux store
   */
  static getTokenStateFromStore() {
    try {
      const persistedData = localStorage.getItem('persist:root');
      if (persistedData) {
        const root = JSON.parse(persistedData);
        const tokens = JSON.parse(root.tokens || '{}');
        return tokens;
      }
    } catch (error) {
      console.error('Error getting token state from store:', error);
    }
    return null;
  }

  /**
   * Calculate optimal refresh schedule
   */
  static calculateRefreshSchedule(creationDate) {
    const timeUntilExpiry = this.getTimeUntilExpiry(creationDate);
    const refreshThreshold = 5 * 60 * 1000; // 5 minutes before expiry
    
    // Schedule refresh 5 minutes before expiry, but at least 1 minute from now
    const timeUntilRefresh = Math.max(
      timeUntilExpiry - refreshThreshold,
      60 * 1000 // minimum 1 minute
    );
    
    return {
      timeUntilRefresh,
      refreshAt: new Date(Date.now() + timeUntilRefresh),
      expiresAt: new Date(Date.now() + timeUntilExpiry)
    };
  }

  /**
   * Validate token structure and data
   */
  static validateTokenData(tokenState) {
    const issues = [];
    
    if (!tokenState) {
      issues.push('No token state found');
      return { isValid: false, issues };
    }
    
    if (!tokenState.accessToken) {
      issues.push('No access token found');
    }
    
    if (!tokenState.creationDate) {
      issues.push('No token creation date found');
    }
    
    if (tokenState.isAuthenticated !== true) {
      issues.push('User not authenticated');
    }
    
    if (tokenState.creationDate && this.isTokenExpired(tokenState.creationDate)) {
      issues.push('Token has expired');
    }
    
    return {
      isValid: issues.length === 0,
      issues,
      healthStatus: this.getTokenHealthStatus(tokenState.creationDate)
    };
  }

  /**
   * Create token refresh warning message
   */
  static createRefreshWarning(creationDate) {
    const timeUntilExpiry = this.getTimeUntilExpiry(creationDate);
    const minutes = Math.floor(timeUntilExpiry / (60 * 1000));
    
    if (minutes <= 1) {
      return 'Your session will expire in less than 1 minute. Refreshing token...';
    }
    
    return `Your session will expire in ${minutes} minute${minutes > 1 ? 's' : ''}. Refreshing token...`;
  }

  /**
   * Debug token information
   */
  static debugTokenInfo(tokenState) {
    if (!tokenState) return 'No token state available';
    
    const info = {
      hasToken: !!tokenState.accessToken,
      isAuthenticated: tokenState.isAuthenticated,
      creationDate: tokenState.creationDate,
      timeUntilExpiry: this.formatTimeUntilExpiry(tokenState.creationDate),
      healthStatus: this.getTokenHealthStatus(tokenState.creationDate),
      shouldRefresh: this.shouldRefreshToken(tokenState.creationDate),
      isExpired: this.isTokenExpired(tokenState.creationDate)
    };
    
    return JSON.stringify(info, null, 2);
  }
}

// Export as default and individual functions for convenience
export const {
  getTimeUntilExpiry,
  shouldRefreshToken,
  isTokenExpired,
  getTokenExpiryDate,
  formatTimeUntilExpiry,
  getTokenHealthStatus,
  getUsernameFromStore,
  getTokenStateFromStore,
  calculateRefreshSchedule,
  validateTokenData,
  createRefreshWarning,
  debugTokenInfo
} = TokenUtils;

export default TokenUtils;